/**
 * Проверка адресов, на которые web уводит пользователя после входа
 * (`?returnUrl=` у входа, регистрации и `/auto-login`).
 *
 * Зачем: после входа к `returnUrl` дописывается JWT. Без проверки ссылка
 * `/login?returnUrl=https://чужой.домен` отдавала токен чужому сайту, а
 * `returnUrl=javascript:…` выполнялся на нашем origin. Поэтому уводим только на
 * свой origin.
 */
import Logger from '@web/common/Logger'
import Token from '@web/common/token'
import { withQueryParams } from '@web/common/url'

/**
 * Свои origin: тот, где открыт ЛК, и origin платформы из окружения. ЛК живёт на
 * домене платформы под `/class/` (anyclass.com, takeclass.io и т. д.), поэтому
 * страницы платформы — тот же origin, и список доменов не нужен.
 * VUE_APP_BASE_URL нужен для дев-стенда, где SPA и платформа на разных хостах.
 */
function ownOrigins (baseUrl: string | undefined): string[] {
  const origins = [window.location.origin]
  const base = baseUrl?.trim()
  if (base) {
    try {
      origins.push(new URL(base).origin)
    } catch {
      // Кривой VUE_APP_BASE_URL — считаем своим только текущий origin.
    }
  }
  return origins
}

function parse (raw: string): URL | null {
  try {
    // Относительный адрес достраиваем от текущего origin — так `//evil.io` и
    // `/\evil.io` разбираются ровно так же, как их разберёт браузер при переходе.
    return new URL(raw, window.location.origin)
  } catch {
    return null
  }
}

/** Адрес ведёт на свой origin по http(s). Относительный путь — свой. */
export function isOwnOriginUrl (raw: string, baseUrl = process.env.VUE_APP_BASE_URL): boolean {
  const url = parse(raw)
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) {
    return false
  }
  return ownOrigins(baseUrl).includes(url.origin)
}

/**
 * Абсолютный адрес для перехода или `null`, если уводить туда нельзя.
 * Принимает сырое значение из query: там может оказаться массив или пусто.
 */
export function sanitizeReturnUrl (raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null
  }
  if (!isOwnOriginUrl(raw)) {
    return null
  }
  return parse(raw)?.toString() ?? null
}

/**
 * Уводит на `returnUrl` с токеном в query — так платформа узнаёт пользователя
 * на своей странице. Чужой или кривой адрес отклоняется: возвращаем `false`,
 * и вызывающий уходит по умолчанию.
 */
export function redirectToReturnUrl (
  raw: unknown,
  navigate: (url: string) => void = (url) => { window.location.href = url }
): boolean {
  const safe = sanitizeReturnUrl(raw)
  if (!safe) {
    if (raw) Logger.warn('returnUrl отклонён: адрес не на своём домене', raw)
    return false
  }

  // withQueryParams: остальной адрес (в т. ч. _ac_cid/_ac_ref) — байт в байт.
  navigate(withQueryParams(safe, { token: Token.get() || null }))
  return true
}
